import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { Zap, Lock, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError(true);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setResetSuccess(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      toast.error(errorMsg);
      if (errorMsg.includes('expired') || errorMsg.includes('invalid')) {
        setTokenError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
        <div className="auth-card animate-slideUp text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Invalid or Expired Link
          </h1>
          
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired.
            Please request a new reset link.
          </p>
          
          <div className="space-y-3">
            <Link to="/forgot-password">
              <Button 
                className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
                data-testid="request-new-link-btn"
              >
                Request New Link
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                variant="outline"
                className="w-full h-12 rounded-xl font-medium"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
        <div className="auth-card animate-slideUp text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Password Reset Successful!
          </h1>
          
          <p className="text-gray-600 mb-6">
            Your password has been successfully reset. 
            You can now log in with your new password.
          </p>
          
          <Button 
            onClick={() => navigate('/login')}
            className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
      <div className="auth-card animate-slideUp">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-gray-900" style={{ fontFamily: 'Manrope' }}>CORE</span>
        </div>
        
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Reset Your Password
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Enter your new password below
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#4A9CC7] focus:ring-[#4A9CC7]"
                data-testid="new-password-input"
              />
            </div>
            <p className="text-xs text-gray-500">At least 6 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-gray-700">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#4A9CC7] focus:ring-[#4A9CC7]"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
            data-testid="reset-password-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>

        <Link to="/login" className="flex items-center justify-center gap-2 text-[#4A9CC7] font-medium hover:underline mt-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default ResetPassword;
